/**
 * PRICING UTILITY MODULE
 * ---------------------
 * This module contains standardized functions for calculating all prices, taxes,
 * deposits, and fees across the application. All components should use these
 * functions to ensure consistent pricing logic.
 * 
 * Key components:
 * - GST (5%): Applied to all products
 * - PST (7%): Applied to eligible products based on product category/type
 * - Bottle Deposit: Applied to bottled beverages
 * - Eco Fee: Applied to products with certain environmental impact
 * 
 * The functions in this module are designed to work with both frontend cart items
 * and backend order items, ensuring prices are consistent throughout the application
 * and match what is displayed in the Ecwid dashboard.
 * 
 * All monetary values are rounded to 2 decimal places to avoid floating point issues.
 */

import { Product, ProductAttribute, Attribute } from '../types/api';
import { CartItem } from '../context/CartContext';

// Tax IDs
export const GST_TAX_ID = 627941127;
export const PST_TAX_ID = 1683536190;

// Tax rates
export const GST_RATE = 0.05; // 5%
export const PST_RATE = 0.07; // 7%

// Attribute IDs
export const BOTTLE_DEPOSIT_ATTRIBUTE_ID = 49703859;
export const ECO_FEE_ATTRIBUTE_ID = 49703860;

/**
 * Rounds a number to two decimal places
 */
function roundToTwo(num: number): number {
  return Math.round((num + Number.EPSILON) * 100) / 100;
}

/**
 * Determines if PST should be applied based on product name
 */
function shouldApplyPST(productName: string): boolean {
  // Products that are exempt from PST usually include basic groceries, books, etc.
  const pstExemptKeywords = ['book', 'grocery', 'food', 'produce', 'medicine'];
  const lowerName = productName.toLowerCase();
  
  // Return false if any exempt keyword is found in the product name
  return !pstExemptKeywords.some(keyword => lowerName.includes(keyword));
}

/**
 * Gets the attribute value amount by ID
 */
function getAttributeAmountById(product: Product, attributeId: number): number {
  if (!product.attributes) return 0;
  
  const attribute = product.attributes.find(attr => String(attr.id) === String(attributeId));
  if (!attribute || attribute.value === undefined) return 0;
  
  return typeof attribute.value === 'number' 
    ? attribute.value 
    : Number(parseFloat(String(attribute.value)).toFixed(2));
}

/**
 * Calculate all fee components for a product
 */
export function calculateFees(product: Product): { 
  gst: number; 
  pst: number; 
  deposit: number; 
  ecoFee: number; 
  tax: number;
  total: number 
} {
  // Make sure we're working with a number price
  const price = Number(product.price);
  
  // Check if GST should be applied based on enabledManualTaxes
  const applyGST = product.tax?.enabledManualTaxes?.includes(GST_TAX_ID) ?? true;
  const applyPST = product.tax?.enabledManualTaxes?.includes(PST_TAX_ID) ?? true;
  
  // Calculate GST (only if in enabledManualTaxes)
  const gst = applyGST ? Number((price * GST_RATE).toFixed(2)) : 0;
  
  // Calculate PST if applicable and in enabledManualTaxes
  const pstEligible = shouldApplyPST(product.name);
  const pst = (applyPST && pstEligible) ? Number((price * PST_RATE).toFixed(2)) : 0;
  
  // Calculate total tax
  const tax = Number((gst + pst).toFixed(2));
  
  // Get deposit and eco fee from product attributes
  const deposit = getAttributeAmountById(product, BOTTLE_DEPOSIT_ATTRIBUTE_ID);
  const ecoFee = getAttributeAmountById(product, ECO_FEE_ATTRIBUTE_ID);
  
  // Calculate total
  const total = Number((price + tax + deposit + ecoFee).toFixed(2));
  
  return { gst, pst, tax, deposit, ecoFee, total };
}

/**
 * Helper to get an attribute value by ID or name
 */
function getAttributeValue(attributes: Attribute[] | ProductAttribute[] | undefined, idOrName: number | string): number {
  if (!attributes || !attributes.length) {
    return 0;
  }

  // First try to find by ID if idOrName is a number
  if (typeof idOrName === 'number') {
    const attributeById = attributes.find(
      attr => attr.id !== undefined && String(attr.id) === String(idOrName)
    );
    
    if (attributeById) {
      return typeof attributeById.value === 'number' 
        ? attributeById.value 
        : parseFloat(String(attributeById.value)) || 0;
    }
    
    // If not found by ID, try matching name based on known attribute IDs
    const attributeName = idOrName === BOTTLE_DEPOSIT_ATTRIBUTE_ID ? 'Bottle Deposit' : 
                          idOrName === ECO_FEE_ATTRIBUTE_ID ? 'Eco Fee' : '';
    
    if (attributeName) {
      const attributeByName = attributes.find(attr => 
        attr.name === attributeName || 
        attr.name?.toLowerCase() === attributeName.toLowerCase()
      );
      
      if (attributeByName) {
        return typeof attributeByName.value === 'number' 
          ? attributeByName.value 
          : parseFloat(String(attributeByName.value)) || 0;
      }
    }
    
    return 0;
  }
  
  // If idOrName is a string (attribute name), search case-insensitive
  const attributeByName = attributes.find(attr => 
    attr.name === idOrName || 
    attr.name?.toLowerCase() === idOrName.toLowerCase()
  );
  
  if (attributeByName) {
    return typeof attributeByName.value === 'number' 
      ? attributeByName.value 
      : parseFloat(String(attributeByName.value)) || 0;
  }
  
  return 0;
}

/**
 * Calculate all fee components for a cart item
 */
export function calculateCartItemFees(
  item: CartItem,
  enabledManualTaxes?: [boolean, boolean]
): {
  subtotal: number;
  gst: number;
  pst: number;
  totalDeposit: number;
  totalEcoFee: number;
  totalTax: number;
  tax: number;
  total: number;
} {
  const subtotal = item.price * item.quantity;
  
  let bottleDeposit = 0;
  let ecoFee = 0;
  
  // Check if item has attributes
  if (item.attributes && item.attributes.length > 0) {
    // Look for bottle deposit and eco fee in attributes
    const bottleDepositAttr = item.attributes.find((attr) => 
      attr.name === 'Bottle Deposit' || 
      (attr.id !== undefined && String(attr.id) === String(BOTTLE_DEPOSIT_ATTRIBUTE_ID))
    );
    
    const ecoFeeAttr = item.attributes.find((attr) => 
      attr.name === 'Eco Fee' || 
      (attr.id !== undefined && String(attr.id) === String(ECO_FEE_ATTRIBUTE_ID))
    );
    
    if (bottleDepositAttr && bottleDepositAttr.value !== undefined) {
      // Extract numeric value from the string or number value
      const depositValue = typeof bottleDepositAttr.value === 'number' 
        ? bottleDepositAttr.value 
        : parseFloat(String(bottleDepositAttr.value).replace(/[^0-9.]/g, ''));
        
      if (!isNaN(depositValue)) {
        bottleDeposit = depositValue * item.quantity;
      }
    }
    
    if (ecoFeeAttr && ecoFeeAttr.value !== undefined) {
      // Extract numeric value from the string or number value
      const feeValue = typeof ecoFeeAttr.value === 'number'
        ? ecoFeeAttr.value
        : parseFloat(String(ecoFeeAttr.value).replace(/[^0-9.]/g, ''));
        
      if (!isNaN(feeValue)) {
        ecoFee = feeValue * item.quantity;
      }
    }
  }
  
  // Check if we should apply GST and PST based on product tax settings
  const applyGST = item.product?.tax?.enabledManualTaxes?.includes(GST_TAX_ID) ?? true;
  const applyPST = item.product?.tax?.enabledManualTaxes?.includes(PST_TAX_ID) ?? true;
  
  // If enabledManualTaxes parameter is provided, override the tax settings
  const useGST = enabledManualTaxes !== undefined ? enabledManualTaxes[0] : applyGST;
  const usePST = enabledManualTaxes !== undefined ? enabledManualTaxes[1] : applyPST;
  
  // Apply GST if enabled by product settings
  const gst = useGST ? roundToTwo(subtotal * GST_RATE) : 0;
  
  // Apply PST only if enabled by product settings and item is eligible
  const pst = usePST && shouldApplyPST(item.name) ? roundToTwo(subtotal * PST_RATE) : 0;
  
  const totalTax = gst + pst;
  
  // Calculate total
  const total = subtotal + totalTax + bottleDeposit + ecoFee;
  
  return {
    subtotal: roundToTwo(subtotal),
    gst: roundToTwo(gst),
    pst: roundToTwo(pst),
    totalDeposit: roundToTwo(bottleDeposit),
    totalEcoFee: roundToTwo(ecoFee),
    totalTax: roundToTwo(totalTax),
    tax: roundToTwo(totalTax), // For compatibility with CartPage.tsx
    total: roundToTwo(total)
  };
}

/**
 * Format currency for display
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD'
  }).format(amount);
}

/**
 * Check if product has a specific category
 */
export const hasCategory = (categoryIds: number[], categoryId: number): boolean => {
  return categoryIds.includes(categoryId);
};

/**
 * Calculate discount percentage
 */
export const calculateDiscount = (originalPrice: number, salePrice: number): number => {
  if (!originalPrice || originalPrice <= salePrice) return 0;
  return Math.round(((originalPrice - salePrice) / originalPrice) * 100);
}; 

/**
 * Format attribute values for API submission
 * Ensures all attribute values are properly formatted as strings
 * and IDs are properly typed for the API
 */
export function formatAttributesForSubmission(
  attributes: Attribute[] | ProductAttribute[] | Array<{ name: string; value: any; id?: number | string }> | undefined
): Array<{ name: string; value: string; id?: number }> {
  if (!attributes || !Array.isArray(attributes)) return [];
  
  return attributes.map(attr => {
    // Convert string IDs to numbers when possible
    let id: number | undefined = undefined;
    if (attr.id !== undefined) {
      id = typeof attr.id === 'number' ? attr.id : Number(attr.id);
      // If ID couldn't be converted to a valid number, omit it
      if (isNaN(id)) id = undefined;
    }
    
    return {
      name: attr.name,
      value: String(attr.value),
      ...(id !== undefined ? { id } : {})
    };
  });
} 